/**
 * Customers → Dated Changes: what a customer gets on particular days, when it differs from the agreement.
 *
 * This is the register that replaces the Fleet console's customer page for the one thing that page does and
 * the ERP did not: "two instead of four until Friday", "skip me next week", "stop the half-litres".
 *
 * WHY IT IS A REGISTER AND NOT A FORM ON THE SUBSCRIPTION
 *
 * Because the question a desk asks is rarely "what has this customer changed" and usually "what is changed
 * for tomorrow, across everybody" — the round is planned per day, not per customer. Both readings are
 * available: this register filters by date, and Subscription 360 shows one customer's own schedule.
 *
 * NO MONEY, DELIBERATELY. A dated change alters what is delivered, never what is charged. What a change is
 * worth is EPIC 14 and there is no price master to ask.
 */
import { CalendarClock, CalendarX2, Minus, Undo2 } from 'lucide-react';
import { StatusBadge } from '../../ui/primitives';
import { asInteger, asText } from '../operations/ActionDialog';
import { when, type OpsDescriptor, type OpsRow } from '../operations/types';

/** The effect, with the icon that makes it readable at a glance in a dense table. */
function effectCell(row: OpsRow) {
  const effect = String(row.effect);
  if (effect === 'QUANTITY') {
    return <StatusBadge tone="info" icon={CalendarClock}>{String(row.quantity_units)} a day</StatusBadge>;
  }
  if (effect === 'PAUSE') {
    return <StatusBadge tone="queued" icon={CalendarX2}>Paused</StatusBadge>;
  }
  return <StatusBadge tone="neutral" icon={Minus}>Removed</StatusBadge>;
}

/** The window, read the way somebody says it out loud. */
function windowCell(row: OpsRow) {
  const from = String(row.effective_from);
  const until = row.effective_until ? String(row.effective_until) : null;
  if (!until) return <span>from {from} <span className="text-muted">onwards</span></span>;
  if (until === from) return <span>{from} <span className="text-muted">only</span></span>;
  return <span>{from} <span className="text-muted">to</span> {until}</span>;
}

export const DATED_CHANGES: OpsDescriptor = {
  id: 'dated-changes', title: 'Dated Changes', captures: false,
  subtitle:
    'What a customer gets on particular days, where it differs from their standing agreement — fewer for a week, paused while they are away, one variant stopped. The day’s planning reads these, so a change here reaches the round.',
  footer: 'A change is cancelled, never deleted: an amendment history that can be erased is not a history. Nothing here carries money',
  viewPermission: 'subscription.view', view: 'dated_changes',
  select: 'id,subscription_id,subscription_code,effect,effective_from,effective_until,quantity_units,channel,reason,variant_code,variant_name,scope,cancelled,cancelled_at,cancel_reason,customer_code,customer_name,in_force_today,created_at,updated_at,row_version',
  searchColumns: ['customer_code', 'customer_name', 'subscription_code', 'variant_code'],
  defaultSort: 'effective_from', defaultSortDirection: 'desc',
  filters: [
    { key: 'effective_from', kind: 'dateRange', label: 'Starts' },
    { key: 'effect', kind: 'select', label: 'Effect', options: [
      { value: 'QUANTITY', label: 'Different quantity' },
      { value: 'PAUSE', label: 'Paused' },
      { value: 'REMOVE', label: 'Variant removed' }] },
    { key: 'in_force_today', kind: 'boolean', label: 'In force today' },
    { key: 'cancelled', kind: 'boolean', label: 'Cancelled' },
    { key: 'variant_code', kind: 'search', label: 'Variant', placeholder: 'Variant code' },
  ],
  columns: [
    { key: 'customer_name', header: 'Customer', width: '15rem' },
    { key: 'customer_code', header: 'Code', width: '11rem', mono: true, optional: true },
    { key: 'effect', header: 'Effect', width: '10rem', render: effectCell },
    { key: 'scope', header: 'Applies to', width: '9rem', mono: true,
      render: (r) => (r.variant_code ? String(r.variant_code) : <span className="text-muted">every variant</span>) },
    { key: 'effective_from', header: 'When', width: '15rem', render: windowCell },
    { key: 'in_force_today', header: 'Today', width: '6rem',
      render: (r) => (r.cancelled ? null : r.in_force_today ? <StatusBadge tone="info" icon={CalendarClock}>Now</StatusBadge> : null) },
    { key: 'channel', header: 'Asked via', width: '8rem',
      render: (r) => String(r.channel).replace(/_/g, ' ').toLowerCase() },
    { key: 'cancelled', header: 'Stands', width: '9rem',
      render: (r) => (r.cancelled
        ? <StatusBadge tone="neutral" icon={Undo2} title={String(r.cancel_reason ?? '')}>Cancelled</StatusBadge>
        : null) },
    { key: 'reason', header: 'Why', optional: true },
    { key: 'cancel_reason', header: 'Cancelled because', optional: true },
    { key: 'created_at', header: 'Recorded', width: '11rem', optional: true, render: (r) => when(r.created_at) },
  ],
  actions: [
    {
      key: 'add', label: 'Record a dated change', icon: CalendarClock, permission: 'subscription.manage',
      rpc: 'add_dated_change', scope: 'global',
      intro: (
        <>
          Three kinds only. <strong>Different quantity</strong> needs one variant and a number of at least one
          — for none, use a pause. <strong>Pause</strong> needs an end date, because a pause without one is a
          stop, and an agreement is stopped by cancelling it. <strong>Remove</strong> has no end date, because
          a removal with one is a pause.
        </>
      ),
      fields: [
        { key: 'subscription_id', label: 'Agreement', kind: 'ref', required: true, view: 'subscriptions', labelColumns: ['code', 'customer_name'] },
        { key: 'effect', label: 'What changes', kind: 'select', required: true, options: [
          { value: 'QUANTITY', label: 'A different quantity' },
          { value: 'PAUSE', label: 'Pause — deliver nothing' },
          { value: 'REMOVE', label: 'Remove a variant for good' }] },
        { key: 'product_variant_id', label: 'Variant', kind: 'ref', view: 'product_variants', labelColumns: ['code', 'name'],
          hint: 'Required for a quantity change or a removal. Leave empty to pause everything' },
        { key: 'quantity_units', label: 'Units a day', kind: 'integer', hint: 'For a quantity change. At least one' },
        { key: 'effective_from', label: 'From', kind: 'date', required: true, hint: 'Inclusive' },
        { key: 'effective_until', label: 'Until', kind: 'date', hint: 'Inclusive. Required for a pause, forbidden for a removal' },
        { key: 'channel', label: 'Asked via', kind: 'select', options: [
          { value: 'OFFICE', label: 'The office' },
          { value: 'WHATSAPP', label: 'WhatsApp' },
          { value: 'DATED_CHANGE', label: 'A dated change from the field' },
          { value: 'INTEGRATION', label: 'Another system' }] },
        { key: 'reason', label: 'Why', kind: 'textarea' },
      ],
      params: (v, _r, key) => ({
        p_subscription_id: asText(v.subscription_id), p_effect: asText(v.effect),
        p_effective_from: asText(v.effective_from), p_product_variant_id: asText(v.product_variant_id),
        p_effective_until: asText(v.effective_until), p_quantity_units: asInteger(v.quantity_units),
        p_channel: asText(v.channel) ?? 'OFFICE', p_reason: asText(v.reason), p_idempotency_key: key,
      }),
      success: (r) => String(r.detail ?? 'Recorded.'),
      invalidates: ['dated-changes', 'subscriptions', 'customer-orders'],
    },
    {
      key: 'cancel', label: 'Cancel this change', icon: Undo2, permission: 'subscription.manage',
      rpc: 'cancel_dated_change', scope: 'row',
      available: (r) => !r.cancelled,
      intro: (
        <>
          The row is kept with your reason. Orders already planned for those days are <strong>not</strong>
          {' '}altered — re-plan the day to apply this.
        </>
      ),
      fields: [{ key: 'reason', label: 'Why', kind: 'textarea', required: true, hint: 'At most 300 characters' }],
      params: (v, r, key) => ({ p_dated_change_id: r?.id, p_reason: asText(v.reason), p_idempotency_key: key }),
      success: (r) => String(r.detail ?? 'Cancelled.'),
      invalidates: ['dated-changes', 'subscriptions'],
    },
  ],
  detail: {
    entityType: 'sales.dated_changes',
    title: (r) => `${String(r.customer_name)} · ${String(r.effect).toLowerCase()}`,
    evidence: false,
  },
};
